import { lazy } from 'react';
import { devRoutes } from './dev';
import { adminRoutes } from 'routes/admin';
import { dashboardRoutes } from 'routes/dashboard';
import { editorRoutes } from 'routes/editor';
import { monitorRoutes } from 'routes/monitor';
import { serviceRoutes } from 'routes/service/index';
import { notificationRoutes } from 'routes/notification';
import { breadcrumbItem } from 'store/breadcrumbsSlice';
import { redirectRoutes } from 'routes/redirect';
import { mobileRoutes } from './mobile';
const ExternalReportGetToken = lazy(() => import('app/extreport/ExternalReportGetToken'));
const ElementViewer = lazy(() => import('components/element-viewer/ElementViewer'));
const IFrameElementViewer = lazy(() => import('components/element-viewer/IFrameElementViewer'));
const GlobalSearch = lazy(() => import('app/search/global/index'));
const SearchTuning = lazy(() => import('app/search/tuning/index'));
const ElementScreenShot = lazy(() => import('components/element-screenshot/ElementScreenShot'));
const ConciergeScreenshot = lazy(() => import('app/concierge/screenshot/ConciergeScreenshot'));
const BenchmarkEditorShell = lazy(() => import('app/concierge/benchmark/BenchmarkEditorShell'));
const BenchmarkList = lazy(() => import('app/concierge/benchmark/BenchmarkList'));
const BenchmarkEditor = lazy(() => import('app/concierge/benchmark/editor/BenchmarkEditor'));
const KgProfileEditorShell = lazy(() => import('app/concierge/kg-profile/KgProfileEditorShell'));
const GuardActivityMonitorShell = lazy(() => import('app/concierge/guard-activity/GuardActivityMonitorShell'));
const ExternalContentFileError = lazy(
    () => import('components/element-viewer/external-content-file/ExternalContentFileError'),
);
const DocumentViewer = lazy(() => import('components/document-viewer/DocumentViewer'));
const PublishingBoard = lazy(() => import('app/content-workflow/PublishingBoard'));
const DenyPage = lazy(() => import('app/auth/deny'));
const LoginPage = lazy(() => import('app/auth/login/index'));
const SignedOut = lazy(() => import('app/auth/signed-out'));
const DatasetViewer = lazy(() => import('components/dataset-viewer/DatasetViewer'));
const ReportAlertRulePage = lazy(() => import('../app/report/report-alert-rule/ReportAlertRulePage'));
const ChangePassword = lazy(() => import('app/auth/change'));
const ResetPassword = lazy(() => import('app/auth/resetpass'));
const ResetPasswordConfirm = lazy(() => import('app/auth/resetpass/confirm'));
const PortalPageViewer = lazy(() => import('app/portal-page-viewer'));

const BlankLayout = lazy(() => import('layouts/BlankLayout'));
const DefaultLayout = lazy(() => import('layouts/DefaultLayout'));
import Error404Page from 'app/error/Error404Page';
import PageRatingPrompt from 'app/page-rating-prompt';

export interface breadcrumbsSettings {
    list?: breadcrumbItem[];
    instanceDataKey?: string | 'noInstanceDataKey'; //default 'name'. Use 'noInstanceDataKey' to  refuse set by instance
}
export interface pageTitleSettings {
    value?: false | string; //false - set default portalName if exist else empty ''
    instanceDataKey?: string | 'noInstanceDataKey'; //default 'name'. Use 'noInstanceDataKey' to  refuse set by instance
}
interface RouteItemProps {
    settingsUrl?: string;
    uid?: string;
    pk?: string;
    pageMenu?: string;
    pageTitle?: false | pageTitleSettings; //false - remove title! Override Default: RouteItemType.name.
    breadcrumbs?: false | breadcrumbsSettings; //false - hide! Default RouteItemType.name
    helpLink?: string; //User Menu -> Help link for page
    passSearchParams?: boolean; //pass search params to request data
}

export type RouteItemType = {
    path: Array<string>;
    name: string; //By default for PageTitle and Breadcrumbs Label
    component: any;
    routeUid?: string; //Unique route identifier, used fot tour
    props?: RouteItemProps;
    layout?: string;
    isPublic?: boolean;
    isPortalPageMenu?: boolean;
    index?: boolean;
    isElementViewer?: boolean;
};

export const layoutRoutes: any[] = [
    {
        name: 'default',
        component: DefaultLayout,
    },
    {
        name: 'blank',
        component: BlankLayout,
    },
];

const indexRoutes: Array<RouteItemType> = [
    ...([
        {
            path: ['*'],
            name: 'error_404.name',
            component: Error404Page,
            props: {
                breadcrumbs: {
                    list: [],
                },
            },
        },
        {
            path: ['/ext-content/:elementId/unavailable/error/:error/fileId?/:fileId?/file?/:fileName?'],
            name: 'extcontent.name',
            component: ExternalContentFileError,
            layout: 'blank',
        },
        {
            path: ['/ext-content/:elementId/*'],
            name: 'extcontent.name',
            routeUid: 'external-content-viewer',
            component: ElementViewer,
            isElementViewer: true,
        },
        {
            path: ['/extreport/:elementId/*'],
            name: 'extreport.name',
            routeUid: 'external-report-viewer',
            component: ElementViewer,
            isElementViewer: true,
        },
        {
            path: ['/chart/:elementId/*'],
            name: 'metric.name',
            routeUid: 'metric-viewer',
            component: ElementViewer,
            isElementViewer: true,
            props: {
                //look diff link by param v6 $helpLinksForPages
                helpLink:
                    'https://help.metricinsights.com/m/Jump_Start_Guide_for__Regular_Users/l/494422-opening-the-full-chart-view',
            },
        },
        {
            path: ['/report/:elementId/*'],
            name: 'report.name',
            routeUid: 'report-viewer',
            component: ElementViewer,
            isElementViewer: true,
            props: {
                helpLink:
                    'https://help.metricinsights.com/m/Creating_Basic_Metrics_and_Reports/l/104406-create-a-simple-non-segmented-report',
            },
        },
        {
            path: [
                '/service/iframe/index/type/:embeddingType/segment?/:segmentValueId?/element/:elementId/navigation_back?/:navigation_back/new_tab?/:new_tab/view?/:view/*',
                '/service/iframe/index/type/:embeddingType/segment?/:segmentValueId?/element/:elementId/view?/:view/*',
                '/service/iframe/index/type/:embeddingType/segment?/:segmentValueId?/element/:elementId/*',
                '/service/iframe/index/type/:embeddingType/element/:elementId/segment?/:segmentValueId?/*',
                '/service/iframe/index/type/:embeddingType/category/:categoryId?/navigation_back?/:navigation_back?/new_tab?/:new_tab?/view?/:view?/*',
                '/service/iframe/index/type/:embeddingType/folder/:folderId?/navigation_back?/:navigation_back?/new_tab?/:new_tab?/view?/:view?/*',
                '/service/iframe/index/type/:embeddingType/navigation_back?/:navigation_back?/new_tab?/:new_tab?/view?/:view?/*',
            ],
            name: 'element.name',
            component: IFrameElementViewer,
            layout: 'blank',
        },
        {
            path: ['content/index/view-document/document/:documentId'],
            name: 'document.name',
            component: DocumentViewer,
        },
        {
            path: [
                // Metric
                '/chart/:elementId/segment/:segmentValueId/uco?/:uco?/thumbnail',
                '/chart/:elementId/segment?/:segmentValueId?/uco?/:uco?/preview/*',
                // Report
                '/report/:elementId/segment?/:segmentValueId?/thumbnail',
                '/report/:elementId/segment?/:segmentValueId?/preview/*',
                // External Report
                '/extreport/:elementId/segment?/:segmentValueId?/preview/*',
                // External Content
                '/ext-content/:elementId/segment?/:segmentValueId?/preview/*',
            ],
            isPublic: true,
            name: '',
            component: ElementScreenShot,
            layout: 'blank',
        },
        {
            path: ['/concierge/screenshot/:hash'],
            isPublic: true,
            name: '',
            component: ConciergeScreenshot,
            layout: 'blank',
        },
        {
            path: ['/extreport/:elementId/get-token/:connectionProfileId'],
            name: '',
            component: ExternalReportGetToken,
            layout: 'blank',
        },
        {
            path: ['/search/global'],
            name: 'Search',
            component: GlobalSearch,
        },
        {
            path: ['/search/tuning'],
            name: 'search_tuning.name',
            component: SearchTuning,
        },
        {
            path: ['/content-workflow/:cwfId?'],
            name: 'publishing.name',
            component: PublishingBoard,
            props: { pageMenu: 'publishing' },
        },
        {
            path: ['/data/editor/benchmark/ui'],
            name: 'Concierge Benchmarks',
            component: BenchmarkEditorShell,
            props: {
                breadcrumbs: {
                    list: [{ label: 'Concierge Benchmarks' }],
                },
                pageTitle: { value: 'Concierge Benchmarks' },
            },
        },
        {
            path: ['/data/editor/concierge-guard-activity/ui'],
            name: 'Guard Activity',
            component: GuardActivityMonitorShell,
            props: {
                breadcrumbs: {
                    list: [{ label: 'Guard Activity' }],
                },
                pageTitle: { value: 'Guard Activity' },
            },
        },
        {
            path: ['/editor/benchmark/react-ui'],
            name: 'Concierge Benchmarks (new)',
            component: BenchmarkList,
            props: {
                breadcrumbs: {
                    list: [{ label: 'Concierge Benchmarks (new)' }],
                },
                pageTitle: { value: 'Concierge Benchmarks (new)' },
            },
        },
        {
            path: [
                '/editor/benchmark/react-ui/:benchmarkId',
                '/editor/benchmark/react-ui/:benchmarkId/:tab',
                '/editor/benchmark/react-ui/:benchmarkId/:tab/:runId',
            ],
            name: 'Concierge Benchmarks (new)',
            component: BenchmarkEditor,
            props: {
                breadcrumbs: {
                    list: [{ label: 'Concierge Benchmarks (new)' }],
                },
                pageTitle: { value: 'Concierge Benchmarks (new)' },
            },
        },
        {
            path: ['/data/editor/kg-profile/ui'],
            name: 'KG Profiles',
            component: KgProfileEditorShell,
            props: {
                breadcrumbs: {
                    list: [{ label: 'KG Profiles' }],
                },
                pageTitle: { value: 'KG Profiles' },
            },
        },
        {
            path: ['/login'],
            name: 'login',
            component: LoginPage,
            layout: 'blank',
            isPublic: true,
            props: { pageTitle: { value: false } },
        },
        {
            path: ['/auth/deny/type?/:type?/*'],
            name: 'Access Denied',
            component: DenyPage,
            isPublic: true,
            props: {
                breadcrumbs: {
                    list: [],
                },
            },
        },
        {
            path: ['/auth/signed-out'],
            name: 'Signed Out',
            component: SignedOut,
            isPublic: true,
            layout: 'blank',
        },
        {
            path: ['/dataset/:datasetId/instance?/:instanceId?/filter?/:filterId?/*'],
            name: 'Dataset Viewer',
            routeUid: 'dataset-viewer',
            component: DatasetViewer,
            props: {
                helpLink:
                    'https://help.metricinsights.com/m/Creating_Basic_Metrics_and_Reports/l/691335-dataset-viewer-select-fields-define-filters',
            },
        },
        {
            path: ['/editor/element/report/:elementId/alert-rule/:report_alert_rule_id'],
            name: 'Report Alert Rule',
            routeUid: 'report-alert-rule',
            component: ReportAlertRulePage,
        },
        {
            path: ['/api'],
            name: 'API Toolkit',
            component: lazy(() => import('app/api-toolkit/index')),
        },
        {
            path: ['/api-token'],
            name: 'API Token',
            component: lazy(() => import('app/api-toolkit/index')),
        },
        {
            path: ['/auth/change'],
            name: 'changepass.name',
            component: ChangePassword,
            isPublic: true,
            layout: 'blank',
        },
        {
            path: ['/auth/resetpass/confirm'],
            name: 'resetpass.name',
            component: ResetPasswordConfirm,
            isPublic: true,
            layout: 'blank',
        },
        {
            path: ['/auth/resetpass'],
            name: 'resetpass.name',
            component: ResetPassword,
            isPublic: true,
            layout: 'blank',
        },
        {
            path: ['/p/*', '/pt/*', '/pl/*'],
            name: 'portal_page.viewer',
            routeUid: 'app-viewer',
            component: PortalPageViewer,
            isPublic: true,
            isPortalPageMenu: true,
            layout: 'blank',
            props: {
                breadcrumbs: {
                    list: [],
                },
            },
        },
        {
            path: ['/ratingprompt/page/prompt/name/:portalPageName'],
            name: 'portal_page.ratingprompt',
            component: PageRatingPrompt,
            isPublic: true,
            layout: 'blank',
        },
    ] as RouteItemType[]),
    ...devRoutes,
    ...adminRoutes,
    ...dashboardRoutes,
    ...editorRoutes,
    ...notificationRoutes,
    ...monitorRoutes,
    ...serviceRoutes,
    ...mobileRoutes,
    ...redirectRoutes, //backward compatibility
];

export default indexRoutes;
